// src/services/KbFaqService.js
import { repo } from "../dbservice/repo";
import { KbFaqCategoryModel } from "../models/KbFaqCategoryModel";

class KbFaqService {
  constructor() {
    this.collectionName = "kbFaqCategories";
  }

  // ── Current user (same pattern as vendorService) ────────────────────────────
  get currentUser() {
    if (typeof window === "undefined") return null;
    try {
      const data = localStorage.getItem("userData");
      return data ? JSON.parse(data) : null;
    } catch (e) {
      console.error("[KbFaqService] Failed to parse userData", e);
      return null;
    }
  }

  // ── Current company (same source repo uses) ─────────────────────────────────
  get companyId() {
    if (typeof window === "undefined") return null;
    try {
      const raw = localStorage.getItem("companyData");
      return raw ? JSON.parse(raw)?.id || null : null;
    } catch {
      return null;
    }
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // ── CATEGORY CRUD ─────────────────────────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════════════

  /**
   * Create a new FAQ category with automatic audit stamping.
   * @param {{ id?, name, audience?, companyId?, questions? }} data
   */
  async create(data = {}, currentUser = {}) {
    const user = currentUser?.email ? currentUser : this.currentUser;
    const targetId = data.id || crypto.randomUUID();

    const payload = {
      ...data,
      id: targetId,
      companyId: data.companyId || user?.companyId || this.companyId || null,
      metadata: {
        ...(data.metadata || {}),
        createdBy: user?.email || "system",
        updatedBy: user?.email || "system",
      },
    };

    return await this.upsertData(targetId, payload, { isCreation: true });
  }

  /**
   * Fetch categories scoped to a company (and optionally audience).
   * @param {{ companyId?, audience? }} opts
   */
  async fetchCategories({ companyId = "", audience = "" } = {}) {
    const cid = companyId || this.companyId;
    const filters = [];
    if (cid)      filters.push({ field: "companyId", value: String(cid) });
    if (audience) filters.push({ field: "audience",  value: audience });

    try {
      return (await repo.fetchData(this.collectionName, filters)) || [];
    } catch (error) {
      console.error("[KbFaqService] Failed to fetch categories:", error);
      return [];
    }
  }

  /** Fetch a single category by id. */
  async fetchCategoryById(id) {
    if (!id) {
      console.error("[KbFaqService] fetchCategoryById: ID missing");
      return null;
    }
    try {
      return (await repo.fetchById(this.collectionName, id)) || null;
    } catch (error) {
      console.error(`[KbFaqService] Failed to fetch category ${id}:`, error);
      return null;
    }
  }

  /** Update an existing FAQ category — preserves creation metadata. */
  async update(id, data = {}, currentUser = {}) {
    if (!id) throw new Error("FAQ category ID is required for update.");
    const user = currentUser?.email ? currentUser : this.currentUser;

    const payload = {
      ...data,
      id,
      metadata: {
        ...(data.metadata || {}),
        updatedBy: user?.email || "system",
      },
    };

    return await this.upsertData(id, payload, { isCreation: false });
  }

  /** Delete a FAQ category (and all its nested FAQs). */
  async delete(id) {
    if (!id) throw new Error("FAQ category ID is required for deletion.");
    try {
      await repo.deleteData(this.collectionName, id);
      return { success: true };
    } catch (error) {
      console.error(`[KbFaqService] Failed to delete category ${id}:`, error);
      throw error;
    }
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // ── FAQ ITEM CRUD (nested in `questions[]`) ──────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════════════

  /** Read all FAQs of a category. */
  async fetchFaqs(categoryId) {
    const cat = await this.fetchCategoryById(categoryId);
    if (!cat) return [];
    const source = cat.details || cat;
    return Array.isArray(source.questions) ? source.questions : [];
  }

  /** Add a FAQ to a category. */
  async addFaq(categoryId, faqData = {}, currentUser = {}) {
    if (!categoryId) throw new Error("categoryId is required to add a FAQ.");

    const { ok, errors } = KbFaqCategoryModel.validateItem(faqData, {
      isCreation: true,
    });
    if (!ok) throw new Error(`Invalid FAQ: ${errors.join("; ")}`);

    const cat = await this.fetchCategoryById(categoryId);
    if (!cat) throw new Error(`FAQ category "${categoryId}" not found.`);

    const source = cat.details || cat;
    const existing = Array.isArray(source.questions) ? source.questions : [];

    const newFaq = {
      id:        `faq_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      question:  (faqData.question || "").trim(),
      answer:    (faqData.answer   || "").trim(),
      tags:      Array.isArray(faqData.tags) ? faqData.tags : [],
      createdAt: new Date().toISOString(),
    };

    await this.update(
      categoryId,
      {
        ...source,
        id: categoryId,
        name: source.name,
        audience: source.audience || "general",
        companyId: source.companyId || this.companyId || "global",
        questions: [...existing, newFaq],
      },
      currentUser,
    );

    return newFaq;
  }

  /** Update an existing FAQ in-place. */
  async updateFaq(categoryId, faqId, updateData = {}, currentUser = {}) {
    if (!categoryId) throw new Error("categoryId is required to update a FAQ.");
    if (!faqId)      throw new Error("faqId is required to update a FAQ.");

    const cat = await this.fetchCategoryById(categoryId);
    if (!cat) throw new Error(`FAQ category "${categoryId}" not found.`);

    const source   = cat.details || cat;
    const existing = Array.isArray(source.questions) ? [...source.questions] : [];
    const idx      = existing.findIndex((f) => f.id === faqId);
    if (idx === -1) throw new Error(`FAQ "${faqId}" not found.`);

    const merged = {
      ...existing[idx],
      ...updateData,
      updatedAt: new Date().toISOString(),
    };

    const { ok, errors } = KbFaqCategoryModel.validateItem(merged, {
      isCreation: false,
    });
    if (!ok) throw new Error(`Invalid FAQ: ${errors.join("; ")}`);

    existing[idx] = merged;

    await this.update(
      categoryId,
      {
        ...source,
        id: categoryId,
        questions: existing,
      },
      currentUser,
    );

    return merged;
  }

  /** Delete a FAQ from a category. */
  async deleteFaq(categoryId, faqId, currentUser = {}) {
    if (!categoryId) throw new Error("categoryId is required to delete a FAQ.");
    if (!faqId)      throw new Error("faqId is required to delete a FAQ.");

    const cat = await this.fetchCategoryById(categoryId);
    if (!cat) throw new Error(`FAQ category "${categoryId}" not found.`);

    const source   = cat.details || cat;
    const existing = Array.isArray(source.questions) ? [...source.questions] : [];
    const next     = existing.filter((f) => f.id !== faqId);
    if (next.length === existing.length) throw new Error(`FAQ "${faqId}" not found.`);

    await this.update(
      categoryId,
      { ...source, id: categoryId, questions: next },
      currentUser,
    );

    return { success: true };
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // ── CORE PERSISTENCE ORCHESTRATOR (vendorService pattern) ────────────────────
  // ══════════════════════════════════════════════════════════════════════════════

  /**
   * Read-before-write metadata preservation, model validation, then persist.
   */
  async upsertData(targetId, data, { isCreation = false } = {}) {
    if (!targetId) throw new Error("Target FAQ category ID is required");

    let finalData = { ...data };

    if (!isCreation) {
      const existing = await this.fetchCategoryById(targetId);
      if (existing) {
        const existingSrc = existing.details || existing;
        finalData = {
          ...existingSrc,
          ...data,
          metadata: {
            ...(existingSrc.metadata || existing.metadata || {}),
            ...(data.metadata || {}),
            createdAt:
              existingSrc.metadata?.createdAt ||
              existingSrc.createdAt ||
              new Date().toISOString(),
            createdBy:
              existingSrc.metadata?.createdBy ||
              existingSrc.createdBy ||
              "system",
          },
        };
      }
    }

    const instance = new KbFaqCategoryModel(
      { ...finalData, id: targetId },
      { isCreation },
    );

    if (!instance.isValid()) {
      throw new Error(
        "Invalid FAQ category payload: id, name, companyId and a valid questions[] array are required.",
      );
    }

    const payload = instance.toPayload();
    const nowIso  = new Date().toISOString();

    payload.metadata = {
      ...(payload.metadata || {}),
      updatedAt: nowIso,
      updatedBy:
        data.metadata?.updatedBy || payload.metadata?.updatedBy || "system",
    };

    return await repo.upsertData(this.collectionName, targetId, payload);
  }
}

export const kbFaqService = new KbFaqService();